import { client } from './api';

/**
 * Documents assembled out of what arrived by fax.
 *
 * A transmission is not a document, which is what this whole surface exists
 * for: twenty pages can arrive as twenty transmissions and one transmission can
 * hold three documents somebody stapled together. Assembling builds a new
 * document from chosen pages of chosen arrivals; editing re-bakes an existing
 * one from its own pages.
 */

export interface FaxDocument {
  id: string;
  name: string;
  page_count: number;
  file_size: number | null;
  version: number;
  agency_clinic_id: string | null;
  /**
   * The newest referral this document was copied into, when it has been. Only
   * the list carries it, which is the one place the answer changes what is on
   * screen.
   */
  intake_id?: string | null;
  /**
   * That referral's own processing status, so a referral still being extracted
   * can be offered differently from one that is ready to work.
   */
  intake_processing_status?: string | null;
}

export interface FaxDocumentPage {
  items: FaxDocument[];
  total: number;
}

/** One page of one source, and the quarter turn to add to it. */
export interface PageSelection {
  source: number;
  page: number;
  rotation: number;
}

interface DocumentDownload {
  url: string;
}

/** The part of the intake creation response this screen uses. */
interface IntakeCreated {
  intake: { id: string };
}

export async function listFaxDocuments(
  limit = 25,
  offset = 0
): Promise<FaxDocumentPage> {
  const response = await client.get<FaxDocumentPage, unknown, true>({
    query: { limit, offset },
    responseType: 'json',
    throwOnError: true,
    url: '/fax-documents',
  });
  return response.data;
}

export async function assembleFaxDocument(input: {
  name: string;
  sources: string[];
  pages: PageSelection[];
}): Promise<FaxDocument> {
  const response = await client.post<FaxDocument, unknown, true>({
    body: input,
    responseType: 'json',
    throwOnError: true,
    url: '/fax-documents',
  });
  return response.data;
}

/**
 * Re-bake a document from its own pages.
 *
 * ``version`` is the one these page numbers were read from. The server refuses
 * a stale one rather than applying them to a document that has since been
 * re-baked, which would drop or turn a different page than the one on screen.
 */
export async function editFaxDocumentPages(
  documentId: string,
  pages: PageSelection[],
  version: number
): Promise<FaxDocument> {
  const response = await client.patch<FaxDocument, unknown, true>({
    body: { pages, version },
    responseType: 'json',
    throwOnError: true,
    url: `/fax-documents/${documentId}/pages`,
  });
  return response.data;
}

export async function renameFaxDocument(
  documentId: string,
  name: string
): Promise<FaxDocument> {
  const response = await client.patch<FaxDocument, unknown, true>({
    body: { name },
    responseType: 'json',
    throwOnError: true,
    url: `/fax-documents/${documentId}`,
  });
  return response.data;
}

export async function getFaxDocumentUrl(documentId: string): Promise<string> {
  const response = await client.get<DocumentDownload, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/fax-documents/${documentId}/download`,
  });
  return response.data.url;
}

/**
 * Take some pages out of a document and make another one of them.
 *
 * The pages move: a ten page fax carrying two referrals becomes a six and a
 * four, with no page in both and none left behind. Both halves are sent rather
 * than one being derived from the other, because the editor can have reordered
 * or turned pages that were never saved, and a server recomputing the
 * remainder would quietly discard that work.
 *
 * ``version`` is the one these page numbers were read from. The server refuses
 * a stale one rather than taking different pages than the ones on screen.
 */
export async function splitFaxDocument(
  documentId: string,
  input: {
    name: string;
    version: number;
    taken: PageSelection[];
    kept: PageSelection[];
  }
): Promise<FaxDocument> {
  const response = await client.post<FaxDocument, unknown, true>({
    body: input,
    responseType: 'json',
    throwOnError: true,
    url: `/fax-documents/${documentId}/split`,
  });
  return response.data;
}

/**
 * Start one referral from one or more assembled documents.
 *
 * Written by hand against the client rather than taken from the generated one,
 * the way the rest of this screen's calls are, so the inbox's API surface stays
 * in one file and a regeneration does not drag unrelated routes into the diff.
 *
 * The pages are copied into the referral rather than shared with it: an intake
 * document gets stamped and removed, and what arrived by fax has to stay as it
 * was received.
 */
export async function createIntakeFromFaxDocuments(
  faxDocumentIds: string[]
): Promise<{ intakeId: string }> {
  const response = await client.post<IntakeCreated, unknown, true>({
    body: { fax_document_ids: faxDocumentIds },
    responseType: 'json',
    throwOnError: true,
    url: '/intake/from-fax-documents',
  });
  return { intakeId: response.data.intake.id };
}
